import { CSSProperties } from 'react'

import { useQuery } from '@apollo/client'
import { Option } from '@joor/design-system'

import { formatRetailerWithLocation } from 'formatters/orders'
import { formatNameWithCode } from 'utils/formatters'
import { getBrandConnectedAccounts } from 'utils/transformations/accounts'
import { relayConnectionToArray } from 'utils/transformations/graphql'
import { naturalSortBy } from 'utils/transformations/sorting'

import { atlasClient } from 'graphql/client'

import {
  ConnectedAccountsQuery,
  RetailerBrandDoorsQuery,
  SecondStepSharedDataQuery,
} from './SecondStep.queries'
import { ConnectedAccount } from './SecondStep.types'

type SharedOrderSettingsData = {
  id: string
  name?: string
  code?: string
}

export const formatDropdownOptions = (
  data: Array<SharedOrderSettingsData>,
): Option[] =>
  data.map((element) => ({
    id: element.id,
    value: element.id,
    label: formatNameWithCode(element?.name, element?.code),
  }))

export const useSecondStepSharedData = (
  isRetailer: boolean,
  retailerId?: string,
) => {
  const { data, loading, error } = useQuery(SecondStepSharedDataQuery, {
    variables: {
      retailerAccountId: retailerId,
      includeRetailer: !!retailerId,
    },
    skip: !retailerId || isRetailer,
  })

  return {
    error,
    loading,
    orderTypes: relayConnectionToArray(data?.viewerBrand?.types),
    brandWarehouses: relayConnectionToArray(data?.warehouses),
    brandDoors: relayConnectionToArray(data?.node?.brandDoorMatches),
    retailerWarehouses: relayConnectionToArray(
      data?.node?.brandWarehouseMatches,
    ),
  }
}

export const useRetailerBrandDoors = (
  includeDoor: boolean,
  isRetailer: boolean,
  brandId?: string,
) => {
  const { data, loading, error } = useQuery(RetailerBrandDoorsQuery, {
    client: atlasClient,
    variables: { brandId },
    skip: includeDoor || !isRetailer || !brandId,
  })

  return {
    error,
    loading,
    doors: relayConnectionToArray(data?.brandDoors),
  }
}

export const formatConnectedAccountsOptions = (
  connectedAccounts: ConnectedAccount[],
) =>
  naturalSortBy(
    connectedAccounts.map((connectedAccount) => ({
      id: connectedAccount.id,
      value: connectedAccount.id,
      label: formatRetailerWithLocation(connectedAccount),
    })),
    'label',
  )

export const useConnectedAccounts = (searchFilter: string) => {
  const { data, loading, error } = useQuery(ConnectedAccountsQuery, {
    variables: { searchFilter },
    skip: (searchFilter?.length || 0) < 3,
  })

  return {
    connectedAccountsOptions: (!loading && !error && data?.connectedAccounts
      ? formatConnectedAccountsOptions(
          getBrandConnectedAccounts(data?.connectedAccounts),
        )
      : []) as Option[],
    connectedAccountsQueryLoading: loading,
  }
}

export const useGetDropdownProps = (
  openedDropdown: string | null,
  setOpenedDropdown: (key: string | null) => void,
) => {
  return (key: string) => ({
    key: `${key}Dropdown`,
    menuPosition: 'fixed',
    menuIsOpen: openedDropdown === key,
    onMenuOpen: () => setOpenedDropdown(key),
    onMenuClose: () => setOpenedDropdown(null),
    styles: {
      menuPortal: (prev: CSSProperties) => ({
        ...prev,
        zIndex: 9999,
      }),
    },
  })
}
